import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import ProjectCard from '../components/ProjectCard';
import CTAButton from '../components/CTAButton';
import LoadingSpinner from '../components/LoadingSpinner';
import { getProjects } from '../api/projects';
import { useTelegram } from '../hooks/useTelegram';
import { Project } from '../types';

const eo: [number, number, number, number] = [0, 0, 0.25, 1];

const ProjectList: React.FC = () => {
  const { hapticImpact } = useTelegram();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getProjects(1);
      setProjects(data.items);
    } catch (err) {
      setError('Не удалось загрузить проекты');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const featured = projects.find(p => p.is_featured) || (projects.length > 0 ? projects[0] : null);
  const rest = projects.filter(p => p.id !== featured?.id);
  const featuredImage = featured?.media?.[0]?.url;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.45, ease: eo }}
      style={{ minHeight: '100vh', position: 'relative' }}
    >
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section style={{ padding: '36px 24px 0' }}>
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.06, ease: eo }}
          style={{
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: '#AEAEB2',
            marginBottom: '14px',
          }}
        >
          Разработка под ключ
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.12, ease: eo }}
          style={{
            fontFamily: "'DM Serif Display', Georgia, serif",
            fontSize: '34px',
            lineHeight: 1.12,
            letterSpacing: '-0.025em',
            color: '#111111',
            marginBottom: '14px',
          }}
        >
          Создаём продукты,{'\n'}которые приносят{' '}
          <em style={{ fontStyle: 'italic', color: '#6E6E73' }}>клиентов</em>
        </motion.h1>

        <motion.div
          initial={{ width: 0 }}
          animate={{ width: '40px' }}
          transition={{ duration: 0.8, delay: 0.4, ease: eo }}
          style={{
            height: '1.5px',
            background: '#111111',
            marginBottom: '16px',
          }}
        />

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.2, ease: eo }}
          style={{
            fontSize: '15px',
            lineHeight: 1.55,
            color: '#6E6E73',
            maxWidth: '320px',
            margin: 0,
          }}
        >
          Боты, мини&#8209;приложения, сайты и&nbsp;сервисы. От&nbsp;идеи до&nbsp;запуска за&nbsp;2–4&nbsp;недели.
        </motion.p>
      </section>

      {/* Trust Strip */}
      <section style={{ padding: '28px 24px 24px', display: 'flex' }}>
        {[
          { num: '50+', main: 'Проектов', sub: 'запущено и работает', delay: 0.35 },
          { num: '4.9', main: 'Из 5.0', sub: 'оценка клиентов', delay: 0.45 },
          { num: '24ч', main: 'На связи', sub: 'отвечаем в день обращения', delay: 0.55 },
        ].map((item, i) => (
          <motion.div
            key={item.main}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: item.delay, ease: eo }}
            style={{
              flex: 1,
              textAlign: 'center',
              position: 'relative',
            }}
          >
            <div style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: '30px', color: '#111111', lineHeight: 1, letterSpacing: '-0.03em' }}>
              {item.num}
            </div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#111111', marginTop: '4px' }}>
              {item.main}
            </div>
            <div style={{ fontSize: '10px', color: '#AEAEB2', marginTop: '2px', lineHeight: 1.3 }}>
              {item.sub}
            </div>
            {i < 2 && (
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '1px',
                  height: '36px',
                  background: 'rgba(0,0,0,0.12)',
                }}
              />
            )}
          </motion.div>
        ))}
      </section>

      {/* Divider */}
      <div style={{ height: '1px', background: 'rgba(0,0,0,0.06)', margin: '0 24px' }} />

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ textAlign: 'center', padding: '48px 24px', color: '#6E6E73' }}
        >
          <p style={{ marginBottom: '16px', fontSize: '14px' }}>{error}</p>
          <button
            onClick={fetchProjects}
            style={{
              padding: '10px 24px',
              borderRadius: '100px',
              border: '1px solid rgba(0,0,0,0.12)',
              backgroundColor: 'transparent',
              color: '#111111',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
            }}
          >
            Попробовать снова
          </button>
        </motion.div>
      ) : projects.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{ textAlign: 'center', padding: '60px 24px', color: '#6E6E73' }}
        >
          <p style={{ fontSize: '14px' }}>Проекты не найдены</p>
        </motion.div>
      ) : (
        <>
          {/* Featured Case Label */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.38, ease: eo }}
            style={{
              padding: '28px 24px 14px',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: '#AEAEB2',
            }}
          >
            Последний кейс
          </motion.div>

          {/* Featured Card */}
          {featured && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.55, delay: 0.38, ease: eo }}
              onClick={() => {
                hapticImpact('light');
                navigate(`/project/${featured.id}`);
              }}
              style={{
                margin: '0 24px 8px',
                borderRadius: '20px',
                overflow: 'hidden',
                background: '#FFFFFF',
                border: '0.5px solid rgba(0,0,0,0.06)',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {featuredImage ? (
                <img
                  src={featuredImage}
                  alt={featured.title}
                  loading="lazy"
                  style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <div style={{ width: '100%', aspectRatio: '4/3', background: '#F6F6F7' }} />
              )}
              <div style={{ padding: '20px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#AEAEB2', marginBottom: '8px' }}>
                  {featured.category}
                </div>
                <div style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: '22px', lineHeight: 1.2, color: '#111111', marginBottom: '6px', letterSpacing: '-0.02em' }}>
                  {featured.title}
                </div>
                <div
                  style={{
                    fontSize: '14px',
                    lineHeight: 1.55,
                    color: '#6E6E73',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {featured.description}
                </div>
                {featured.result_number && featured.result_label && (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginTop: '14px',
                      padding: '6px 12px',
                      background: '#F6F6F7',
                      borderRadius: '100px',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#111111',
                    }}
                  >
                    <span style={{ width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <path d="M12 19V5M5 12l7-7 7 7" />
                      </svg>
                    </span>
                    {featured.result_number} {featured.result_label}
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginTop: '14px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#111111',
                  }}
                >
                  {featured.bot_url ? 'Открыть в Telegram' : 'Перейти на сайт'}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </motion.div>
          )}

          {/* More Works */}
          {rest.length > 0 && (
            <>
              <div style={{ height: '1px', background: 'rgba(0,0,0,0.06)', margin: '24px 24px 0' }} />
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, delay: 0.48, ease: eo }}
                style={{
                  padding: '28px 24px 14px',
                  fontSize: '11px',
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: '#AEAEB2',
                }}
              >
                Другие проекты
              </motion.div>
              <div style={{ paddingBottom: '8px' }}>
                <div
                  className="hide-scrollbar"
                  style={{
                    display: 'flex',
                    gap: '12px',
                    padding: '0 24px 24px',
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                  }}
                >
                  {rest.map((project, index) => (
                    <ProjectCard key={project.id} project={project} index={index} />
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Bottom Spacer */}
      <div style={{ height: '88px' }} />

      {/* Fixed CTA */}
      <CTAButton label="Обсудить проект" floating />
    </motion.div>
  );
};

export default ProjectList;
